import * as React from 'react'
import { useMemo, useState } from 'react'
import { AccountDto } from '@halo/dtos/account.dto'
import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Flex, Form, Input, notification, Select, Skeleton, Switch } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import moment from 'moment-timezone'
import { simpleNotification } from '@halo/_utils/notifications'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT, SETTINGS_WELL_FORM_LAYOUT } from '@halo/constants'
import * as randomstring from 'randomstring'
import { getSupportedTimezones } from '@halo/_utils/moment'

interface IAccountDetailsEditor {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IAccountDetailsEditorFormValues {
    name?: string
    timezone?: string
    default_notification_landing_url?: string
}

const computeChangedAccountValues = (
    account: AccountDto,
    formValues: IAccountDetailsEditorFormValues,
    urlEnabled: boolean,
): Partial<AccountDto> => {
    const values: Partial<AccountDto> = {}

    values.name = formValues.name?.trim()
    if (values.name === account.name) {
        delete values.name
    }

    if (!urlEnabled) {
        values.default_notification_landing_url = null
    } else {
        values.default_notification_landing_url = formValues.default_notification_landing_url?.trim()
    }
    if (values.default_notification_landing_url === account.default_notification_landing_url) {
        delete values.default_notification_landing_url
    }

    values.timezone = formValues.timezone
    if (values.timezone === account.timezone) {
        delete values.timezone
    }

    return values
}

export const AccountDetailsEditor: React.FunctionComponent<IAccountDetailsEditor> = (props) => {
    const { account } = props
    const accountService = useMemo(() => Container.get(AccountService), [])
    const tzOptions = useMemo(() => getSupportedTimezones(), [])

    const [form] = Form.useForm<IAccountDetailsEditorFormValues>()
    const [urlEnabled, setUrlEnabled] = useState<boolean>(!!account.default_notification_landing_url)

    const defaultTz = account.timezone ?? moment.tz.guess()
    const defaultLandingUrl = account.default_notification_landing_url ?? 'https://{{domain_name}}/'

    // Subscribe to value changes so the wrapping Drawer's disableSubmit recomputes as the user edits.
    Form.useWatch([], form)

    const currentValues = computeChangedAccountValues(account, form.getFieldsValue(), urlEnabled)

    const handleSubmit = async () => {
        const mergedValues = computeChangedAccountValues(account, form.getFieldsValue(), urlEnabled)

        const { ok, data } = await accountService.patch(account.id, mergedValues, true)

        if (ok) {
            props.onAccountUpdated(data)
            props.onClose(null)
        }
    }

    const handleValidateAndSubmit = async () => {
        try {
            await form.validateFields()
            await handleSubmit()
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            className={classnames('adf-details-editor')}
            title="Update Details"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleValidateAndSubmit}
            disableSubmit={Object.keys(currentValues).length === 0}
            onClose={props.onClose}
            open={props.visible}
        >
            <Form
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                form={form}
                initialValues={{
                    name: account.name,
                    timezone: defaultTz,
                    default_notification_landing_url: defaultLandingUrl,
                }}
                onFinish={handleValidateAndSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well title="Organization Information" mode="ghost" footerMode="hidden" padding="none">
                    <Form.Item
                        label="Org Name"
                        name="name"
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: 'Organization name is required.',
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item label="Default Time Zone" name="timezone">
                        <Select showSearch={true} placeholder="Select a time zone">
                            {tzOptions.map((tz) => (
                                <Select.Option key={tz} value={tz}>
                                    {tz}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Default Landing URL"
                        extra={
                            <span>
                                <InfoCircleOutlined /> Setting a default landing URL will automatically fill this
                                value in the “Landing URL” field during organization notification creation.
                            </span>
                        }
                    >
                        <Flex gap={8} align="center">
                            <Switch
                                checked={urlEnabled}
                                onChange={(checked) => {
                                    setUrlEnabled(checked)
                                    if (checked) {
                                        form.setFieldsValue({
                                            default_notification_landing_url: defaultLandingUrl,
                                        })
                                    } else {
                                        form.resetFields(['default_notification_landing_url'])
                                    }
                                }}
                            />
                            {urlEnabled && (
                                <Form.Item name="default_notification_landing_url" noStyle={true}>
                                    <Input style={{ flex: 1 }} />
                                </Form.Item>
                            )}
                        </Flex>
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IAccountDetailsWell {
    loading?: boolean
    account: AccountDto
    title?: string
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IDWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountDetailsWell extends React.Component<IAccountDetailsWell, IDWState> {
    public state: IDWState = {
        showDrawer: false,
    }

    protected appState: AppState

    constructor(props: IAccountDetailsWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Details"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Form
                                {...SETTINGS_WELL_FORM_LAYOUT}
                                colon={false}
                                requiredMark={false}
                                labelAlign="left"
                                size="small"
                            >
                                {this.renderInformationWell()}
                            </Form>

                            <AccountDetailsEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.props.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderInformationWell() {
        const { account } = this.props
        return (
            <Well.Section title="Organization Information">
                <Form.Item label="Org Name">{account.name}</Form.Item>

                <Form.Item label="Default Timezone">{account.timezone || '(Not Set)'}</Form.Item>

                <Form.Item label="Default Landing URL">
                    {account.default_notification_landing_url || '(Not Set)'}
                </Form.Item>

                <Form.Item label="Created">
                    {moment(account.created_at).format('LLL')}
                    <span className="lowlight"> by </span>
                    {account.created_by_user_name}
                </Form.Item>

                <Form.Item label="Last Updated">
                    {moment(account.updated_at).format('LLL')}
                    <span className="lowlight"> by </span>
                    {account.updated_by_user_name}
                </Form.Item>
            </Well.Section>
        )
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
